import { breakpoints } from '@/lib/utils';
import { Component, defineComponent } from '@decify/cell-shopify/client';
import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination, Thumbs } from 'swiper/modules';
import './Slider.scss';
export const sliderName = 'Slider';
export const sliderTagName = 'cell-component-slider';
export const sliderSlideChangeEvent = 'slideChange';
export class Slider extends Component {
    classNames = {
        base: 'Slider',
        slider: 'Slider__Slider',
        controls: 'Slider__Controls',
        pagination: 'Slider__Pagination',
        navigationButton: 'Slider__NavigationButton',
    };
    slider;
    sliderOptions;
    thumbsConnected;
    constructor() {
        super(sliderName);
        this.handleResize = this.handleResize.bind(this);
        this.handleSlideChange = this.handleSlideChange.bind(this);
        this.slider = null;
        this.thumbsConnected = false;
        this.sliderOptions = {
            containerModifierClass: 'Slider--',
            wrapperClass: 'Slider__Wrapper',
            slideClass: 'SlideItem',
            slideActiveClass: 'SlideItem--Active',
            slideNextClass: 'SlideItem--Next',
            slidePrevClass: 'SlideItem--Prev',
            modules: [Autoplay, Navigation, Pagination, Thumbs],
            // TODO: Make this work.
            // direction: 'vertical',
            on: {
                init: (swiper) => this.handleResize(swiper),
                resize: (swiper) => this.handleResize(swiper),
                slideChange: (swiper) => this.handleSlideChange(swiper),
            },
            autoplay: true,
            grabCursor: true,
            breakpoints: Object.entries(breakpoints).reduce((acc, [breakpointKey, breakpointValue]) => {
                acc[breakpointValue] = {
                    enabled: this.props.enabled[breakpointKey] ?? true,
                    slidesPerView: this.props.slidesPerView[breakpointKey] ?? 1,
                    spaceBetween: this.props.spaceBetween[breakpointKey] ?? 0,
                    centerInsufficientSlides: this.props.centerInsufficientSlides[breakpointKey] ?? true,
                    autoplay: this.props.autoplay[breakpointKey]
                        ? {
                            delay: this.props.autoplayDelay[breakpointKey],
                            pauseOnMouseEnter: true,
                        }
                        : false,
                    loop: this.props.loop[breakpointKey] ?? false,
                    navigation: this.props.navigation[breakpointKey] && this.navigationElements
                        ? {
                            disabledClass: 'Slider__NavigationButton--Disabled',
                            prevEl: this.navigationElements.prev,
                            nextEl: this.navigationElements.next,
                        }
                        : false,
                    pagination: this.props.pagination[breakpointKey] && this.paginationElement
                        ? {
                            paginationDisabledClass: 'Slider__Pagination--Disabled',
                            bulletClass: 'Slider__PaginationBullet',
                            bulletActiveClass: 'Slider__PaginationBullet--Active',
                            modifierClass: 'Slider__Pagination--',
                            horizontalClass: 'Slider__Pagination--Horizontal',
                            el: this.paginationElement,
                            clickable: true,
                        }
                        : false,
                };
                return acc;
            }, {}),
        };
    }
    componentDidMount() {
        // TODO: Lazyload swiper
        if (this.props.sliderPaginationStyle === 'thumbnails') {
            const swiper = new Swiper(this.thumbsSliderElement, {
                containerModifierClass: 'ThumbsSlider--',
                wrapperClass: 'ThumbsSlider__Wrapper',
                slideClass: 'ThumbsSlider__SlideItem',
                spaceBetween: 16,
                centerInsufficientSlides: true,
                slidesPerView: 'auto',
                grabCursor: true,
            });
            this.slider = new Swiper(this.sliderElement, {
                ...this.sliderOptions,
                thumbs: {
                    swiper: swiper,
                    slideThumbActiveClass: 'ThumbsSlider__SlideItem--Active',
                    thumbsContainerClass: 'ThumbsSlider',
                },
            });
        }
        else {
            this.slider = new Swiper(this.sliderElement, this.sliderOptions);
        }
    }
    handleResize(swiper) {
        const hasMoreSlidesThanVisible = swiper.slides.length > Number(swiper.params.slidesPerView) ||
            this.wrapperElement.scrollWidth > this.wrapperElement.offsetWidth;
        if (this.navigationElements) {
            this.navigationElements.prev.style.display = hasMoreSlidesThanVisible ? '' : 'none';
            this.navigationElements.next.style.display = hasMoreSlidesThanVisible ? '' : 'none';
        }
        if (this.controlsElements) {
            this.controlsElements.style.display = hasMoreSlidesThanVisible ? '' : 'none';
        }
    }
    handleSlideChange(swiper) {
        this.publish(sliderSlideChangeEvent, {
            activeIndex: swiper.activeIndex,
        });
        if (swiper.params.loop)
            return;
        if (swiper.activeIndex >= swiper.slides.length - 1) {
            this.navigationElements?.next.querySelector('button')?.setAttribute('disabled', '');
        }
        else {
            this.navigationElements?.next.querySelector('button')?.removeAttribute('disabled');
        }
        if (swiper.activeIndex <= 0) {
            this.navigationElements?.prev.querySelector('button')?.setAttribute('disabled', '');
        }
        else {
            this.navigationElements?.prev.querySelector('button')?.removeAttribute('disabled');
        }
    }
    get thumbsSliderElement() {
        const thumbsSliderElement = this.querySelector('.ThumbsSlider');
        if (!thumbsSliderElement || !(thumbsSliderElement instanceof HTMLElement)) {
            throw new Error('Thumbs slider element not found');
        }
        return thumbsSliderElement;
    }
    get sliderElement() {
        const sliderElement = this.querySelector('.Slider__Slider');
        if (!sliderElement || !(sliderElement instanceof HTMLElement)) {
            throw new Error('Slider element not found');
        }
        return sliderElement;
    }
    get wrapperElement() {
        const wrapperElement = this.querySelector('.Slider__Wrapper');
        if (!wrapperElement || !(wrapperElement instanceof HTMLElement)) {
            throw new Error('Wrapper element not found');
        }
        return wrapperElement;
    }
    get controlsElements() {
        const controlsElement = this.querySelector('.Slider__Controls');
        if (controlsElement && !(controlsElement instanceof HTMLElement)) {
            throw new Error('Controls element is not an HTMLElement');
        }
        return controlsElement;
    }
    get navigationElements() {
        const prevNavigationButtonElement = this.querySelector('.Slider__NavigationButton--Prev');
        const nextNavigationButtonElement = this.querySelector('.Slider__NavigationButton--Next');
        if (!prevNavigationButtonElement || !nextNavigationButtonElement) {
            return null;
        }
        if (!(prevNavigationButtonElement instanceof HTMLElement) ||
            !(nextNavigationButtonElement instanceof HTMLElement)) {
            throw new Error('Navigation button elements are not HTMLElements');
        }
        return {
            prev: prevNavigationButtonElement,
            next: nextNavigationButtonElement,
        };
    }
    get paginationElement() {
        const paginationElement = this.querySelector('.Slider__Pagination');
        if (paginationElement && !(paginationElement instanceof HTMLElement)) {
            throw new Error('Pagination element is not an HTMLElement');
        }
        return paginationElement;
    }
}
defineComponent(sliderTagName, Slider);
